/*---------------------------------------------------------------------------------------------
 *  Copyright (c) 2022 Vienna Hypertext Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

type Options = {
  hue?: [number, number]; // degree on the color wheel from 0 to 360. 0 is red, 120 is green, 240 is blue.
  sat?: [number, number]; // saturation percentage value; 0% means a shade of gray and 100% is the full color.
  lit?: [number, number]; // lightness percentage value; 0% is black, 100% is white.
};

/**
 * Returns an HSL color code for a string. Optionally specify HSL ranges.
 */
export function stringToHSL(str: string, opts: Options): string {
  opts = opts || {};
  opts.hue = opts.hue || [0, 360];
  opts.sat = opts.sat || [75, 100];
  opts.lit = opts.lit || [40, 60];

  const range = (hash: number, min: number, max: number) => {
    const diff = max - min;
    const x = ((hash % diff) + diff) % diff;
    return x + min;
  };

  if (str.length === 0) return "#000000";

  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
    hash &= hash;
  }

  const h = range(hash, opts.hue[0], opts.hue[1]);
  const s = range(hash, opts.sat[0], opts.sat[1]);
  const l = range(hash, opts.lit[0], opts.lit[1]);

  return `hsl(${h}, ${s}%, ${l}%)`;
}
